import {
    AccountApi,
    ClassificationTierApi,
    ConfigMatrixApi,
    Configuration as ConfigurationKt,
    FxRateApi,
    HTTPHeaders,
    HTTPRequestInit, KeyValueApi,
    KPIApi,
    PortfolioApi,
    RefdataApi,
    RequestOpts,
    SegmentAccountApi,
    SegmentApi,
    SegmentClassificationTierTargetApi, SegmentParameterApi, SegmentTargetApi
} from "../../api-client-kt/src";

import {
    Configuration as ConfigurationPy,
    PortfolioManagementAPIsApi,
    TechExAGORAAPIsApi,
} from "../../api-client-py/src";
import {Configuration as ConfigurationKtAdmin, MyProfileApi, UserProfileApi} from "../../api-client-kt/src";
import {AuthService} from '../security/auth.service';
import {Provider, signal} from '@angular/core';
import {BaseAPI as BaseAPIKt} from "../../api-client-kt/src/runtime";
import {BaseAPI as BaseAPIKtAdmin} from "../../api-client-kt/src/runtime";
import {BaseAPI as BaseAPIPy} from "../../api-client-py/src/runtime";
import {RequestUtils} from '../lib/requests';
import {ApiEnvironment, ApiServiceType} from '../app.constants';
import {LoadingSpinnerInterceptor} from './loading-spinner-interceptor';

export interface ApiEnvironmentDescription {
    name: string;
    environment: ApiEnvironment | null;
    hostPattern: string | null;
}

export class ApiConfiguration {

    static environments: ApiEnvironmentDescription[] = [
        {name: 'Default', environment: null, hostPattern: null },
        {name: 'Localhost', environment: 'local', hostPattern: 'localhost'},
        {name: 'etractive', environment: 'etractive', hostPattern: '{service}.k3s1.etractive.de'},
        {name: 'Development', environment: 'dev', hostPattern: 'dev-{service}.generali.co.uk'},
        {name: 'Pre-Production', environment: 'preprod', hostPattern: 'preprod-{service}.generali.co.uk'},
        {name: 'Production', environment: 'prod', hostPattern: '{service}.generali.co.uk'}
    ];

    private static _envApiKt= signal<ApiEnvironment>(null);
    static readonly envApiKt = ApiConfiguration._envApiKt.asReadonly();

    private static _envApiPy= signal<ApiEnvironment>(null);
    static readonly envApiPy = ApiConfiguration._envApiPy.asReadonly();

    private static headers: HTTPHeaders = {
        // 'X-API-Client': 'Portfolio Management UI'
    };

    private static serviceMiddlewares = [new LoadingSpinnerInterceptor()];

    private static splitHostAndDomain(uri: string): { prefix: string | null, host: string, domain: string | null } {
        // split as in pattern ({prefix}-)?{host}.{domain}
        const regex = /^(?<prefix>[^-.]+-)?(?<host>[^.]+)(\.(?<domain>.+))?$/;
        const url = new URL(uri);
        const match = url.hostname.match(regex);

        if (!match || !match.groups) {
            throw new Error(`Invalid hostname: ${url.hostname}`);
        }

        return {
            prefix: match.groups['prefix'] ?? null,
            host: match.groups['host'],
            domain: match.groups['domain'] ?? null
        };
    }

    private static serviceEndpointBaseUri(hostPattern: string, serviceType: ApiServiceType): string {
        if (hostPattern === 'localhost') {
            switch (serviceType) {
                case 'kt': return 'http://localhost:8080';
                case 'py': return 'http://localhost:8000';
                default: throw new Error("Unknown service type: " + serviceType);
            }
        } else {
            const serviceHost = serviceType === 'kt' ? "pfmservice" : 'pfmpython';
            const hostname = hostPattern.replace('{service}', serviceHost);
            return `https://${hostname}`;
        }
    }

    private static apiConfigKt = new ConfigurationKt({
        // apiKey: (name: string) => "api-key-for-testing",
        accessToken: () => {
            const token = AuthService.accessTokenStatic();
            return token;
        },
        headers: ApiConfiguration.headers,
        basePath: undefined // will be set dynamically
    });

    private static apiConfigPy = new ConfigurationPy({
        accessToken: () => AuthService.accessTokenStatic(),
        headers: ApiConfiguration.headers,
        basePath: undefined // will be set dynamically
    });

    private static asKtApi<T extends BaseAPIKtAdmin>(apiClass: new (config: ConfigurationKtAdmin) => T): new (config: ConfigurationKt) => BaseAPIKt {
        return apiClass as any;
    }

    static getBasePathKt(): string {
        return ApiConfiguration.apiConfigKt.basePath;
    }

    static getBasePathPy(): string {
        return ApiConfiguration.apiConfigPy.basePath;
    }

    private static guessHostPattern(): string {
        const domainAndHost = ApiConfiguration.splitHostAndDomain(document.baseURI);
        const domain = domainAndHost.domain;
        const prefix = domainAndHost.prefix ?? '';
        return domain ? `${prefix}{service}.${domain}` : domainAndHost.host;
    }

    private static getApiEnvBaseHostPattern(environment: ApiEnvironment, serviceType: ApiServiceType): string {
        const envCfg = ApiConfiguration.environments.find(env => env.environment === environment);
        return envCfg?.hostPattern ?? ApiConfiguration.guessHostPattern();
    }

    private static loadApiEnvConfiguration(environment: ApiEnvironment, serviceType: ApiServiceType): string {
        const serviceHostPattern = ApiConfiguration.getApiEnvBaseHostPattern(environment, serviceType);
        return ApiConfiguration.serviceEndpointBaseUri(serviceHostPattern, serviceType);
    }

    static loadApiProviderUrlConfiguration() {
        const ktServiceHost = ApiConfiguration.loadApiEnvConfiguration(null, 'kt');
        const pyServiceHost = ApiConfiguration.loadApiEnvConfiguration(null, 'py');

        console.log(`Using API host for KT: ${ktServiceHost}`);
        console.log(`Using API host for Python: ${pyServiceHost}`);

        (ApiConfiguration.apiConfigKt as any).configuration.basePath = ktServiceHost;
        (ApiConfiguration.apiConfigPy as any).configuration.basePath = pyServiceHost;
    }

    static initApiProviders(): Provider[] {
        ApiConfiguration.loadApiProviderUrlConfiguration();

        const createKtApiClient = <T extends BaseAPIKt>(apiClass: new (config: ConfigurationKt) => T) => {
            return new apiClass(ApiConfiguration.apiConfigKt).withMiddleware(...ApiConfiguration.serviceMiddlewares);
        }

        const createPyApiClient = <T extends BaseAPIPy>(apiClass: new (config: ConfigurationPy) => T) => {
            return new apiClass(ApiConfiguration.apiConfigPy).withMiddleware(...ApiConfiguration.serviceMiddlewares);
        }

        return [
            {provide: RefdataApi, useValue: createKtApiClient(RefdataApi)},
            {provide: PortfolioApi, useValue: createKtApiClient(PortfolioApi)},
            {provide: AccountApi, useValue: createKtApiClient(AccountApi)},
            {provide: FxRateApi, useValue: createKtApiClient(FxRateApi)},
            {provide: SegmentApi, useValue: createKtApiClient(SegmentApi)},
            {provide: KeyValueApi, useValue: createKtApiClient(KeyValueApi)},
            {provide: SegmentParameterApi, useValue: createKtApiClient(SegmentParameterApi)},
            {provide: KPIApi, useValue: createKtApiClient(KPIApi)},
            {provide: SegmentAccountApi, useValue: createKtApiClient(SegmentAccountApi)},
            {provide: SegmentTargetApi, useValue: createKtApiClient(SegmentTargetApi)},
            {provide: ConfigMatrixApi, useValue: createKtApiClient(ConfigMatrixApi)},
            {provide: ClassificationTierApi, useValue: createKtApiClient(ClassificationTierApi)},
            {provide: SegmentClassificationTierTargetApi, useValue: createKtApiClient(SegmentClassificationTierTargetApi)},

            {provide: UserProfileApi, useValue: createKtApiClient(ApiConfiguration.asKtApi(UserProfileApi))},
            {provide: MyProfileApi, useValue: createKtApiClient(ApiConfiguration.asKtApi(MyProfileApi))},

            {provide: PortfolioManagementAPIsApi, useValue: createPyApiClient(PortfolioManagementAPIsApi)},
            {provide: TechExAGORAAPIsApi, useValue: createPyApiClient(TechExAGORAAPIsApi)},
        ];
    }

    static setApiEnvPy(environment: ApiEnvironment) {
        const pyServiceHost = ApiConfiguration.loadApiEnvConfiguration(environment, 'py');
        console.log(`Using API host for Python: ${pyServiceHost}`);
        (ApiConfiguration.apiConfigPy as any).configuration.basePath = pyServiceHost;
        ApiConfiguration._envApiPy.set(environment);
    }

    static setApiEnvKt(environment: ApiEnvironment) {
        const ktServiceHost = ApiConfiguration.loadApiEnvConfiguration(environment, 'kt');
        console.log(`Using API host for KT: ${ktServiceHost}`);
        (ApiConfiguration.apiConfigKt as any).configuration.basePath = ktServiceHost;
        ApiConfiguration._envApiKt.set(environment);
    }

    static setDevToolsRoleHeader(header: string | null | undefined) {
        if (header) {
            RequestUtils.setHeader(ApiConfiguration.headers, 'X-API-Permissions', header);
        } else {
            RequestUtils.deleteHeader(ApiConfiguration.headers, 'X-API-Permissions');
        }
    }

    static markAsSilentRequest(requestContext: { init: HTTPRequestInit, context: RequestOpts }): Promise<RequestInit> {
        const init = requestContext.init;
        if (init.headers) {
            RequestUtils.setHeader(init.headers, 'X-API-Silent', 'true');
            RequestUtils.setHeader(init.headers, 'X-Request-Log', 'false');
        } else {
            init.headers = { 'X-API-Silent': 'true', 'X-Request-Log': 'false' };
        }
        return Promise.resolve(init);
    }
}
